import React, { useState, useEffect } from 'react';
import {
  Box,
  Paper,
  Typography,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TablePagination,
  Button,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Chip,
  IconButton,
  Tooltip,
  Alert,
  Card,
  CardContent,
  Grid,
  InputAdornment,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Switch,
  FormControlLabel
} from '@mui/material';
import {
  Add as AddIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  Search as SearchIcon,
  FilterList as FilterIcon,
  LocalOffer as CouponIcon,
  Percent as PercentIcon,
  AttachMoney as MoneyIcon,
  Visibility as ViewIcon,
  Refresh as RefreshIcon,
  BarChart as BarChartIcon,
  CardGiftcard as GiftIcon
} from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import { useApp } from '../../context/AppContext';
import { appConfig } from '../../config/app';

const CouponList = () => {
  const navigate = useNavigate();
  const { actions } = useApp();
  
  const [coupons, setCoupons] = useState([]);
  const [loading, setLoading] = useState(true);
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [search, setSearch] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [discountTypeFilter, setDiscountTypeFilter] = useState('all');
  const [totalCount, setTotalCount] = useState(0);
  const [deleteDialog, setDeleteDialog] = useState({ open: false, coupon: null });
  const [usageModal, setUsageModal] = useState({ open: false, usage: {}, updatedAt: null, loading: false });

  useEffect(() => {
    fetchCoupons();
  }, [page, rowsPerPage, search, statusFilter, discountTypeFilter]);

  const fetchCoupons = async () => {
    setLoading(true);
    try {
      const token = localStorage.getItem(appConfig.storage.token);
      const params = new URLSearchParams({
        page: page + 1,
        limit: rowsPerPage,
        search: search,
        status: statusFilter,
        discountType: discountTypeFilter
      });

      const response = await fetch(`${appConfig.api.baseURL}/coupons?${params}`, {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });

      const data = await response.json();

      if (data.success) {
        setCoupons(data.coupons);
        setTotalCount(data.total || data.coupons.length);
      } else {
        actions.showSnackbar(data.message || 'Failed to fetch coupons', 'error');
      }
    } catch (error) {
      console.error('Error fetching coupons:', error);
      actions.showSnackbar('Error fetching coupons', 'error');
    } finally {
      setLoading(false);
    }
  };

  const handleDeleteCoupon = async (couponId) => {
    try {
      const token = localStorage.getItem(appConfig.storage.token);
      const response = await fetch(`${appConfig.api.baseURL}/coupons/${couponId}`, {
        method: 'DELETE',
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });

      const data = await response.json();

      if (data.success) {
        actions.showSnackbar('Coupon deleted successfully!', 'success');
        fetchCoupons();
      } else {
        actions.showSnackbar(data.message || 'Failed to delete coupon', 'error');
      }
    } catch (error) {
      console.error('Error deleting coupon:', error);
      actions.showSnackbar('Error deleting coupon', 'error');
    }
    setDeleteDialog({ open: false, coupon: null });
  };

  const handleToggleStatus = async (couponId, currentStatus) => {
    try {
      const token = localStorage.getItem(appConfig.storage.token);
      const response = await fetch(`${appConfig.api.baseURL}/coupons/${couponId}/toggle-status`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify({ status: !currentStatus })
      });

      const data = await response.json();

      if (data.success) {
        actions.showSnackbar(`Coupon ${!currentStatus ? 'activated' : 'deactivated'} successfully!`, 'success');
        fetchCoupons();
      } else {
        actions.showSnackbar(data.message || 'Failed to update coupon status', 'error');
      }
    } catch (error) {
      console.error('Error updating coupon status:', error);
      actions.showSnackbar('Error updating coupon status', 'error');
    }
  };

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('en-IN', {
      style: 'currency',
      currency: 'INR'
    }).format(amount);
  };

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString('en-IN', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const getDiscountDisplay = (coupon) => {
    if (coupon.discountType === 'percentage') {
      return `${coupon.discountValue}% off`;
    }
    if (coupon.discountType === 'free_sku') {
      return `Free: ${coupon.freeSku || '—'}`;
    }
    return `${formatCurrency(coupon.discountValue)} off`;
  };

  const getStatusColor = (status, expiryDate) => {
    if (!status) return 'default';
    if (new Date(expiryDate) < new Date()) return 'error';
    return 'success';
  };

  const getStatusText = (status, expiryDate) => {
    if (!status) return 'Inactive';
    if (new Date(expiryDate) < new Date()) return 'Expired';
    return 'Active';
  };

  const isExpired = (expiryDate) => {
    return new Date(expiryDate) < new Date();
  };

  const handleShowUsage = async () => {
    setUsageModal(prev => ({ ...prev, open: true, loading: true }));
    try {
      const token = localStorage.getItem(appConfig.storage.token);
      const response = await fetch(`${appConfig.api.baseURL}/coupons/usage/totals`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      const data = await response.json();
      if (data.success) {
        setUsageModal(prev => ({
          ...prev,
          usage: data.usage || {},
          updatedAt: data.updatedAt || null,
          loading: false
        }));
      } else {
        actions.showSnackbar(data.message || 'Failed to load usage', 'error');
        setUsageModal(prev => ({ ...prev, loading: false }));
      }
    } catch (error) {
      console.error('Error fetching coupon usage:', error);
      actions.showSnackbar('Error loading coupon usage', 'error');
      setUsageModal(prev => ({ ...prev, loading: false }));
    }
  };

  const handleCloseUsageModal = () => {
    setUsageModal({ open: false, usage: {}, updatedAt: null, loading: false });
  };

  return (
    <Box sx={{ p: 3 }}>
      <Paper sx={{ p: 3 }}>
        {/* Header */}
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
          <Box sx={{ display: 'flex', alignItems: 'center' }}>
            <CouponIcon sx={{ mr: 2, color: 'primary.main' }} />
            <Typography variant="h4" component="h1">
              Coupon Management
            </Typography>
          </Box>
          <Box sx={{ display: 'flex', gap: 2 }}>
            <Button
              variant="outlined"
              startIcon={<BarChartIcon />}
              onClick={handleShowUsage}
              size="large"
            >
              Show usage
            </Button>
            <Button
              variant="contained"
              startIcon={<AddIcon />}
              onClick={() => navigate('/super-admin/coupons/add')}
              size="large"
            >
              Add Coupon
            </Button>
          </Box>
        </Box>

        {/* Filters */}
        <Card sx={{ mb: 3 }}>
          <CardContent>
            <Grid container spacing={2} alignItems="center">
              <Grid item xs={12} md={4}>
                <TextField
                  fullWidth
                  placeholder="Search coupons..."
                  value={search}
                  onChange={(e) => setSearch(e.target.value)}
                  InputProps={{
                    startAdornment: (
                      <InputAdornment position="start">
                        <SearchIcon />
                      </InputAdornment>
                    ),
                  }}
                />
              </Grid>
              <Grid item xs={12} md={3}>
                <FormControl fullWidth>
                  <InputLabel>Status</InputLabel>
                  <Select
                    value={statusFilter}
                    onChange={(e) => setStatusFilter(e.target.value)}
                    label="Status"
                  >
                    <MenuItem value="all">All Status</MenuItem>
                    <MenuItem value="active">Active</MenuItem>
                    <MenuItem value="inactive">Inactive</MenuItem>
                    <MenuItem value="expired">Expired</MenuItem>
                  </Select>
                </FormControl>
              </Grid>
              <Grid item xs={12} md={3}>
                <FormControl fullWidth>
                  <InputLabel>Discount Type</InputLabel>
                  <Select
                    value={discountTypeFilter}
                    onChange={(e) => setDiscountTypeFilter(e.target.value)}
                    label="Discount Type"
                  >
                    <MenuItem value="all">All Types</MenuItem>
                    <MenuItem value="percentage">Percentage</MenuItem>
                    <MenuItem value="fixed">Fixed Amount</MenuItem>
                    <MenuItem value="free_sku">Free SKU</MenuItem>
                  </Select>
                </FormControl>
              </Grid>
              <Grid item xs={12} md={2}>
                <Button
                  fullWidth
                  variant="outlined"
                  startIcon={<RefreshIcon />}
                  onClick={fetchCoupons}
                >
                  Refresh
                </Button>
              </Grid>
            </Grid>
          </CardContent>
        </Card>

        {/* Table */}
        <TableContainer component={Paper} variant="outlined">
          <Table>
            <TableHead>
              <TableRow>
                <TableCell>Code</TableCell>
                <TableCell>Discount</TableCell>
                <TableCell>Expiry Date</TableCell>
                <TableCell>Status</TableCell>
                <TableCell>Usage limit</TableCell>
                <TableCell>Stores</TableCell>
                <TableCell>Created</TableCell>
                <TableCell align="center">Actions</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {loading ? (
                <TableRow>
                  <TableCell colSpan={8} align="center">
                    <Typography>Loading coupons...</Typography>
                  </TableCell>
                </TableRow>
              ) : coupons.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={8} align="center">
                    <Typography color="text.secondary">
                      No coupons found
                    </Typography>
                  </TableCell>
                </TableRow>
              ) : (
                coupons.map((coupon) => (
                  <TableRow key={coupon.id} hover>
                    <TableCell>
                      <Box sx={{ display: 'flex', alignItems: 'center' }}>
                        <Chip
                          label={coupon.code}
                          color="primary"
                          variant="outlined"
                          size="small"
                          sx={{ mr: 1 }}
                        />
                        {coupon.discountType === 'percentage' ? (
                          <PercentIcon sx={{ fontSize: '1rem', color: 'primary.main' }} />
                        ) : coupon.discountType === 'free_sku' ? (
                          <GiftIcon sx={{ fontSize: '1rem', color: 'primary.main' }} />
                        ) : (
                          <MoneyIcon sx={{ fontSize: '1rem', color: 'primary.main' }} />
                        )}
                      </Box>
                    </TableCell>
                    <TableCell>
                      <Box>
                        <Typography variant="body2" fontWeight="bold">
                          {getDiscountDisplay(coupon)}
                        </Typography>
                        {coupon.minimumOrderAmount && (
                          <Typography variant="caption" color="text.secondary">
                            Min: {formatCurrency(coupon.minimumOrderAmount)}
                          </Typography>
                        )}
                        {coupon.maximumDiscount && (
                          <Typography variant="caption" color="text.secondary" display="block">
                            Max: {formatCurrency(coupon.maximumDiscount)}
                          </Typography>
                        )}
                      </Box>
                    </TableCell>
                    <TableCell>
                      <Typography variant="body2">
                        {formatDate(coupon.expiryDate)}
                      </Typography>
                      {isExpired(coupon.expiryDate) && (
                        <Chip
                          label="Expired"
                          color="error"
                          size="small"
                          sx={{ mt: 0.5 }}
                        />
                      )}
                    </TableCell>
                    <TableCell>
                      <Chip
                        label={getStatusText(coupon.status, coupon.expiryDate)}
                        color={getStatusColor(coupon.status, coupon.expiryDate)}
                        size="small"
                      />
                    </TableCell>
                    <TableCell>
                      <Typography variant="body2">
                        {coupon.usageLimit != null && coupon.usageLimit !== '' ? coupon.usageLimit : 'Unlimited'}
                      </Typography>
                    </TableCell>
                    <TableCell>
                      <Chip
                        label={
                          Array.isArray(coupon.allowedStoreIds) && coupon.allowedStoreIds.length > 0
                            ? `${coupon.allowedStoreIds.length} store(s)`
                            : 'All stores'
                        }
                        size="small"
                        variant="outlined"
                        color={
                          Array.isArray(coupon.allowedStoreIds) && coupon.allowedStoreIds.length > 0
                            ? 'primary'
                            : 'default'
                        }
                      />
                    </TableCell>
                    <TableCell>
                      <Typography variant="body2">
                        {formatDate(coupon.createdAt)}
                      </Typography>
                    </TableCell>
                    <TableCell align="center">
                      <Box sx={{ display: 'flex', gap: 1, justifyContent: 'center' }}>
                        <Tooltip title="Edit Coupon">
                          <IconButton
                            size="small"
                            onClick={() => navigate(`/super-admin/coupons/edit/${coupon.id}`)}
                            color="primary"
                          >
                            <EditIcon />
                          </IconButton>
                        </Tooltip>
                        
                        <Tooltip title={coupon.status ? 'Deactivate' : 'Activate'}>
                          <IconButton
                            size="small"
                            onClick={() => handleToggleStatus(coupon.id, coupon.status)}
                            color={coupon.status ? 'warning' : 'success'}
                          >
                            <Switch
                              checked={coupon.status && !isExpired(coupon.expiryDate)}
                              size="small"
                              disabled={isExpired(coupon.expiryDate)}
                            />
                          </IconButton>
                        </Tooltip>
                        
                        <Tooltip title="Delete Coupon">
                          <IconButton
                            size="small"
                            onClick={() => setDeleteDialog({ open: true, coupon })}
                            color="error"
                          >
                            <DeleteIcon />
                          </IconButton>
                        </Tooltip>
                      </Box>
                    </TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        </TableContainer>

        {/* Pagination */}
        <TablePagination
          component="div"
          count={totalCount}
          page={page}
          onPageChange={(event, newPage) => setPage(newPage)}
          rowsPerPage={rowsPerPage}
          onRowsPerPageChange={(event) => {
            setRowsPerPage(parseInt(event.target.value, 10));
            setPage(0);
          }}
          rowsPerPageOptions={[5, 10, 25, 50]}
        />
      </Paper>

      {/* Delete Confirmation Dialog */}
      <Dialog
        open={deleteDialog.open}
        onClose={() => setDeleteDialog({ open: false, coupon: null })}
      >
        <DialogTitle>Delete Coupon</DialogTitle>
        <DialogContent>
          <Typography>
            Are you sure you want to delete the coupon "{deleteDialog.coupon?.code}"?
            This action cannot be undone.
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button
            onClick={() => setDeleteDialog({ open: false, coupon: null })}
          >
            Cancel
          </Button>
          <Button
            onClick={() => handleDeleteCoupon(deleteDialog.coupon?.id)}
            color="error"
            variant="contained"
          >
            Delete
          </Button>
        </DialogActions>
      </Dialog>

      {/* Coupon usage modal */}
      <Dialog
        open={usageModal.open}
        onClose={handleCloseUsageModal}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>Coupon usage (global)</DialogTitle>
        <DialogContent>
          {usageModal.loading ? (
            <Box sx={{ py: 4, textAlign: 'center' }}>
              <Typography color="text.secondary">Loading usage...</Typography>
            </Box>
          ) : Object.keys(usageModal.usage).length === 0 ? (
            <Typography color="text.secondary">
              No coupon usage recorded yet.
            </Typography>
          ) : (
            <>
              <TableContainer component={Paper} variant="outlined" sx={{ mt: 1 }}>
                <Table size="small">
                  <TableHead>
                    <TableRow>
                      <TableCell><strong>Coupon code</strong></TableCell>
                      <TableCell align="right"><strong>Times used</strong></TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {Object.entries(usageModal.usage)
                      .sort((a, b) => (b[1] || 0) - (a[1] || 0))
                      .map(([code, count]) => (
                        <TableRow key={code}>
                          <TableCell>{code}</TableCell>
                          <TableCell align="right">{count}</TableCell>
                        </TableRow>
                      ))}
                  </TableBody>
                </Table>
              </TableContainer>
              {usageModal.updatedAt && (
                <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 2 }}>
                  Last updated: {formatDate(usageModal.updatedAt)}
                </Typography>
              )}
            </>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseUsageModal}>Close</Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default CouponList; 